import React, { useEffect, useRef, useState } from "react";
import {
  Dimensions,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

export interface SingleSelectDropdownProps {
  label: string;
  value: string;
  options: string[];
  emptyLabel?: string;
  allowEmpty?: boolean;
  optionLabels?: Record<string, string>;
  onChange: (value: string) => void;
}

const MENU_HEIGHT = 280;
const MIN_MENU_WIDTH = 220;

const SingleSelectDropdown: React.FC<SingleSelectDropdownProps> = ({ label, value, options, emptyLabel = `All ${label.toLowerCase()}s`, allowEmpty = true, optionLabels, onChange }) => {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<any>(null);
  const selectedLabel = value ? optionLabels?.[value] ?? value : emptyLabel;

  const updatePosition = () => {
    const { height: screenHeight, width: screenWidth } = Dimensions.get("window");
    const getSafePosition = (x: number, width: number, top: number) => {
      const menuWidth = Math.min(Math.max(width || MIN_MENU_WIDTH, MIN_MENU_WIDTH), Math.max(screenWidth - 16, 0));
      const left = Math.min(Math.max(x, 8), Math.max(screenWidth - menuWidth - 8, 8));
      return { top, left, width: menuWidth };
    };

    if (Platform.OS === "web" && triggerRef.current?.getBoundingClientRect) {
      const rect = triggerRef.current.getBoundingClientRect();
      const top = screenHeight - rect.bottom < MENU_HEIGHT + 8 ? rect.top - MENU_HEIGHT - 4 : rect.bottom + 4;
      setDropPos(getSafePosition(rect.left, rect.width, top));
      return;
    }

    triggerRef.current?.measureInWindow?.((x: number, y: number, width: number, height: number) => {
      const top = screenHeight - (y + height) < MENU_HEIGHT + 8 ? y - MENU_HEIGHT - 4 : y + height + 4;
      setDropPos(getSafePosition(x, width, top));
    });
  };

  useEffect(() => {
    if (!open || Platform.OS !== "web") return undefined;
    const handlePositionChange = () => updatePosition();
    window.addEventListener("scroll", handlePositionChange, true);
    window.addEventListener("resize", handlePositionChange);
    updatePosition();
    return () => {
      window.removeEventListener("scroll", handlePositionChange, true);
      window.removeEventListener("resize", handlePositionChange);
    };
  }, [open]);

  const toggleOpen = () => {
    if (!open) updatePosition();
    setOpen((current) => !current);
  };

  const selectOption = (option: string) => {
    onChange(option);
    setOpen(false);
  };

  return (
    <View style={styles.filter}>
      <Text style={styles.filterLabel}>{label}</Text>
      <Pressable ref={triggerRef} onPress={toggleOpen} style={[styles.dropdownTrigger, open && styles.dropdownTriggerActive]} accessibilityRole="button" accessibilityLabel={`${label}: ${selectedLabel}`} accessibilityState={{ expanded: open }}>
        <Text style={[styles.dropdownText, !value && styles.dropdownPlaceholder]} numberOfLines={1}>{selectedLabel}</Text>
        <MaterialIcons name={open ? "keyboard-arrow-up" : "keyboard-arrow-down"} size={20} color="#585858" />
      </Pressable>
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.dropdownOverlay} onPress={() => setOpen(false)}>
          <Pressable onPress={(event) => event.stopPropagation()} style={[styles.dropdownMenu, { top: dropPos.top, left: dropPos.left, width: dropPos.width || MIN_MENU_WIDTH }]}>
            <View style={styles.dropdownHeader}>
              <Text style={styles.dropdownMenuTitle}>{label} Filter</Text>
            </View>
            <ScrollView style={styles.dropdownList} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              {allowEmpty && <Pressable onPress={() => selectOption("")} style={[styles.dropdownOption, !value && styles.dropdownOptionActive]} accessibilityRole="radio" accessibilityState={{ selected: !value }}>
                <View style={[styles.dropdownCheck, !value && styles.dropdownCheckSelected]}>{!value && <MaterialIcons name="check" size={13} color="#FFFFFF" />}</View>
                <Text style={styles.dropdownOptionText} numberOfLines={1}>{emptyLabel}</Text>
              </Pressable>}
              {options.map((option) => <Pressable key={option} onPress={() => selectOption(option)} style={[styles.dropdownOption, value === option && styles.dropdownOptionActive]} accessibilityRole="radio" accessibilityState={{ selected: value === option }}><View style={[styles.dropdownCheck, value === option && styles.dropdownCheckSelected]}>{value === option && <MaterialIcons name="check" size={13} color="#FFFFFF" />}</View><Text style={styles.dropdownOptionText} numberOfLines={1}>{optionLabels?.[option] ?? option}</Text></Pressable>)}
            </ScrollView>
            <View style={styles.dropdownFooter}><Text style={styles.dropdownFooterText}>{value ? "1 selected" : "No selection"}</Text></View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  filter: { minWidth: 150, maxWidth: 280 },
  filterLabel: { color: "#656565", fontSize: 11, fontWeight: "600", marginBottom: 5 },
  dropdownTrigger: { minHeight: 40, flexDirection: "row", alignItems: "center", justifyContent: "space-between", borderWidth: 1, borderColor: "#C9D6E5", borderRadius: 6, paddingHorizontal: 11, backgroundColor: "#FFFFFF" },
  dropdownTriggerActive: { borderColor: "#005CBC", backgroundColor: "#F8FBFF" },
  dropdownText: { flex: 1, color: "#2D2D2D", fontSize: 13, marginRight: 8 },
  dropdownPlaceholder: { color: "#2D2D2D", fontWeight: "600" },
  dropdownOverlay: { flex: 1, backgroundColor: "rgba(0, 0, 0, 0.15)" },
  dropdownMenu: { position: Platform.OS === "web" ? ("fixed" as any) : "absolute", maxHeight: Dimensions.get("window").height * 0.45, overflow: "hidden", backgroundColor: "#FFFFFF", borderRadius: 8, zIndex: 9999, elevation: 8, ...(Platform.OS === "web" ? ({ boxShadow: "0 4px 16px rgba(0,0,0,0.16)" } as any) : { shadowColor: "#000", shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.16, shadowRadius: 10 }) },
  dropdownHeader: { borderBottomWidth: 1, borderBottomColor: "#E9ECEF" },
  dropdownMenuTitle: { color: "#2D2D2D", fontSize: 12, fontWeight: "700", paddingHorizontal: 12, paddingTop: 10, paddingBottom: 9 },
  dropdownList: { maxHeight: Dimensions.get("window").height * 0.28 },
  dropdownOption: { minHeight: 42, flexDirection: "row", alignItems: "center", gap: 10, borderRadius: 5, paddingHorizontal: 12, paddingVertical: 9 },
  dropdownOptionActive: { backgroundColor: "#EEF5FF" },
  dropdownCheck: { width: 18, height: 18, alignItems: "center", justifyContent: "center", borderWidth: 1.5, borderColor: "#ADB5BD", borderRadius: 4 },
  dropdownCheckSelected: { backgroundColor: "#0B5FC6", borderColor: "#0B5FC6" },
  dropdownOptionText: { flex: 1, color: "#343A40", fontSize: 12, fontWeight: "500" },
  dropdownFooter: { borderTopWidth: 1, borderTopColor: "#E9ECEF", paddingVertical: 8, backgroundColor: "#F8F9FA" },
  dropdownFooterText: { color: "#6B6E72", fontSize: 11, textAlign: "center", fontWeight: "500" },
});

export default SingleSelectDropdown;
